import { Injectable, inject } from '@angular/core';
import {
  IndirizzoSpedizione,
} from '@models/indirizzo.model';
import { IConsensi } from '@models/privacy.model';
import {
  EMPTY_USER,
  IProfilo,
  ProfiloRuolo,
  RuoloAttivo,
  User
} from '@models/user.model';
import { AppStore } from '@services/store/app.store';
import { BehaviorSubject } from 'rxjs';

/**
 * Servizio repository per la gestione dell'utente e dei profili.
 * Si occupa di recuperare, salvare e aggiornare i dati utente e profilo
 * nello store dell'applicazione, oltre a fornire metodi di utilità per la gestione
 * dei ruoli, consensi privacy e indirizzi di spedizione.
 *
 * Utilizza AppStore per la gestione dello stato dell'applicazione.
 * I profili sono ora gestiti direttamente nell'utente per evitare duplicazione.
 *
 * @example
 * // Recupera l'utente corrente
 * const user = userRepository.getUser();
 *
 * // Salva un nuovo profilo
 * userRepository.saveProfilo(profilo);
 */
@Injectable({
  providedIn: 'root'
})
export class UserRepository {
  private appStore = inject(AppStore);

  /**
   * Recupera l'utente dallo store dell'applicazione.
   * Restituisce EMPTY_USER se l'utente non è presente nello store.
   * @returns L'utente recuperato dallo store o EMPTY_USER se non presente.
   */
  getUser(): User {
    const user = this.appStore.user();
    // Console.log('UserRepository.getUser', 'recuperato utente');
    return user || new User(EMPTY_USER);
  }

  /**
   * Salva l'utente nello store dell'applicazione.
   * @param {User} user - L'utente da salvare nello store.
   */
  saveUser(user: User): void {
    // Console.log('UserRepository.saveUser', 'salvato utente');
    this.appStore.setUser(user);
  }

  /**
   * Aggiorna l'utente corrente nello store.
   * @param {BehaviorSubject<User>} userSubject - BehaviorSubject dell'utente (deprecato, mantenuto per compatibilità).
   * @param {User} user - Istanza dell'utente da aggiornare nello store.
   * @param {BehaviorSubject<IProfilo | null>} [profileSubject] - BehaviorSubject del profilo (deprecato, mantenuto per compatibilità).
   * @param {ProfiloRuolo} [profileRole] - Ruolo del profilo (deprecato, mantenuto per compatibilità).
   */
  updateCurrentUser(
    userSubject: BehaviorSubject<User>, 
    user: User, 
    profileSubject?: BehaviorSubject<IProfilo | null>,
    profileRole?: ProfiloRuolo
  ): void {
    // Mantiene la compatibilità con il codice esistente
    this.saveUser(user);
    
    // Aggiorna anche il BehaviorSubject se fornito
    if (userSubject) {
      userSubject.next(user);
    }
  }

  /**
   * Aggiorna i consensi privacy dell'utente nello store.
   * @param {IConsensi} consensi - Nuovi consensi privacy da impostare.
   */
  setUserConsensiPrivacy(consensi: IConsensi): void {
    const user = this.getUser();
    if (user) {
      user.aggiornaUserConsensiPrivacy(consensi);
      this.saveUser(user);
    }
  }

  /**
   * Aggiorna i consensi privacy dell'utente nello store.
   * @param {IConsensi} consensi - Nuovi consensi privacy da impostare.
   */
  setAnagraficaConsensiPrivacy(consensi: IConsensi): void {
    const user = this.getUser();
    if (user) {
      user.aggiornaAnagraficaConsensiPrivacy(consensi);
      this.saveUser(user);
    }
  }

  /**
   * Aggiorna l'indirizzo di spedizione dell'utente nello store.
   * @param {IndirizzoSpedizione} indirizzo - Nuovo indirizzo di spedizione da impostare.
   */
  setUserIndirizzoSpedizione(indirizzo: IndirizzoSpedizione): void {
    const user = this.getUser();
    if (user) {
      user.aggiornaIndirizzoSpedizione(indirizzo);
      this.saveUser(user);
    }
  }
//-------------------------------------END USER -------------------------------------/

//------------------------------------- PROFILO -------------------------------------/
  /**
   * Verifica se il profilo è attivo per il ruolo specificato.
   * Un profilo è considerato attivo se:
   * - Il ruolo corrisponde
   * - L'ID anagrafica è valido (> 0)
   * - Non è bloccato (bloccato === 0)
   * - Ha un ID segnalatore valido (> 0)
   * 
   * @param {IProfilo} profilo - Profilo da verificare.
   * @param {ProfiloRuolo} ruolo_da_verificare - Ruolo da verificare.
   * @returns {boolean} True se il profilo è attivo per il ruolo specificato.
   */
  isProfileActive(profilo: IProfilo, ruolo_da_verificare: ProfiloRuolo): boolean {
    let attivo = false;
    
    if (
       profilo &&
       profilo.ruolo === ruolo_da_verificare &&
       profilo.id_anagrafica > 0 &&
       profilo.bloccato === false &&
       profilo.id_segnalatore > 0
     ) {
       attivo = true;
     }

     // Console.log('UserRepository.isProfileActive()', ruolo_da_verificare + ':' + attivo, 'std' );

     return attivo;
  }

  /**
   * Salva l'array completo dei profili nell'utente.
   * @param {IProfilo[]} profili - Array di profili da salvare nell'utente.
   */
  saveProfili(profili: IProfilo[]): void {
    // Utilizza AppStore.setProfili per sincronizzare i profili con l'utente
    this.appStore.setProfili(profili);
  }

  /**
   * Aggiorna un singolo profilo nell'utente mantenendo gli altri profili invariati.
   * @param {IProfilo} profilo - Profilo da aggiornare nell'utente.
   */
  saveProfilo(profilo: IProfilo): void {
    const user = this.getUser();
    if (user && user.profili) {
      const updatedProfili = user.profili.map(p => 
        p.ruolo === profilo.ruolo ? profilo : p
      );
      // Utilizza AppStore.setProfili per sincronizzare i profili aggiornati
      this.appStore.setProfili(updatedProfili);
    }
  }

  /**
   * Recupera il profilo dall'utente in base al ruolo specificato.
   * @param {RuoloAttivo} ruolo - Ruolo del profilo da recuperare.
   * @returns {IProfilo | null} Il profilo corrispondente o null se non esiste o se il ruolo è 'indefinito'.
   */
  getProfilo(ruolo: RuoloAttivo): IProfilo | null {
    if(ruolo === '') {
      return null;
    }
    const user = this.getUser();
    if (user && user.profili) {
      return user.profili.find(p => p.ruolo === ruolo) || null;
    }
    return null;
  }

  /**
   * Recupera tutti i profili dell'utente.
   * @returns {IProfilo[]} Array dei profili dell'utente o array vuoto se non presenti.
   */
  getProfili(): IProfilo[] {
    const user = this.getUser();
    return user && user.profili ? user.profili : [];
  }

  /**
   * Recupera il ruolo attivo dell'utente dallo store.
   * @returns {RuoloAttivo | null} Il ruolo attivo corrente o null se non impostato.
   */
  getActiveRole(): RuoloAttivo | null {
    const ruolo = this.appStore.activeRole();
    // Console.log('UserRepository.getActiveRole', 'ruolo attivo: ' + ruolo);
    return ruolo;
  }

  /**
   * Verifica se il ruolo attivo nell'applicazione corrisponde al ruolo specificato.
   * @param {RuoloAttivo} ruolo - Ruolo da confrontare con quello attivo.
   * @returns {boolean} True se il ruolo attivo corrisponde al ruolo specificato.
   */
  isActiveRole(ruolo: RuoloAttivo): boolean {
    const ruolo_attivo = this.getActiveRole();
    return !!ruolo_attivo && ruolo_attivo === ruolo;
  }

  /**
   * Verifica se esiste un profilo attivo per il ruolo specificato tra i ruoli possibili.
   * I ruoli possibili sono: 'promotore', 'convenzionato', 'manifestazione'.
   * 
   * @param {RuoloAttivo} ruolo - Ruolo da verificare.
   * @returns {boolean} True se esiste un profilo attivo per il ruolo specificato.
   */
  findActiveRole(ruolo: RuoloAttivo): boolean {
    const ruoliPossibili: ProfiloRuolo[] = [
      'promotore',
      'convenzionato',
      'manifestazione',
    ];
    
    for (const r of ruoliPossibili) {
      const profilo = this.getProfilo(r);
      if (profilo && this.isProfileActive(profilo, r) && r === ruolo) {
        return true;
      }
    }
    return false;
  }

  /**
   * Aggiorna il profilo corrente nell'utente.
   * @param {BehaviorSubject<IProfilo | null>} profileSubject - BehaviorSubject del profilo (deprecato, mantenuto per compatibilità).
   * @param {IProfilo | null} profile - Profilo da impostare come corrente nell'utente.
   */
  updateCurrentProfile(profileSubject: BehaviorSubject<IProfilo | null>, profile: IProfilo | null): void {
    if (profile) {
      this.saveProfilo(profile);
      
      // Aggiorna anche il BehaviorSubject se fornito
      if (profileSubject) {
        profileSubject.next(profile);
      }
    }
  }

  /**
   * Imposta il ruolo attivo dell'utente nello store dell'applicazione.
   * @param {RuoloAttivo} ruolo - Ruolo da impostare come attivo nello store.
   */
  setActiveRole(ruolo: RuoloAttivo): void {
    // Console.log('UserRepository.setActiveRole', 'impostato ruolo attivo: ' + ruolo);
    this.appStore.setActiveRole(ruolo);
  }

}
